import { useMutation } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { withJWTClient } from "../connectClient";
import { CONNECT_API_V1_BASE_URL, withUpdateMask } from "../gateway";

import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

interface GatewaySiteRecommendationsSlice {
  id?: string;
  rj_id?: string;
  recommendations_ignored_products: string[];
  recommendations_ignored_categories: string[];
}

function fromGatewaySiteRecommendations(
  data: GatewaySiteRecommendationsSlice,
): AccountSiteDetails {
  return {
    id: data.rj_id || data.id || "",
    recommendations_ignored_products: data.recommendations_ignored_products,
    recommendations_ignored_categories: data.recommendations_ignored_categories,
  };
}

// --- Types ---
export interface AccountSiteDetails {
  // Add other fields when needed from the API response
  id: string;
  recommendations_ignored_products: string[];
  recommendations_ignored_categories: string[];
}

export interface UpdateAccountSiteDetailsPayload {
  recommendations_ignored_products?: string[];
  recommendations_ignored_categories?: string[];
}

// --- Query Keys ---
export const accountSiteDetailsKeys = {
  all: (siteId: string) => ["account-site-details", siteId] as const,
};

// --- Query Options ---
export const accountSiteDetailsQueryOptions = (siteId: string) => ({
  queryKey: accountSiteDetailsKeys.all(siteId),
  queryFn: () => getAccountSiteDetails(siteId),
  enabled: !!siteId,
});

// --- API Functions ---
export async function getAccountSiteDetails(
  siteId: string,
): Promise<AccountSiteDetails> {
  const response = await withJWTClient((client) =>
    client.get<GatewaySiteRecommendationsSlice>(
      `${CONNECT_API_V1_BASE_URL}/sites/${siteId}`,
    ),
  );
  return fromGatewaySiteRecommendations(response.data);
}

export const updateAccountSiteDetails = async (
  siteId: string,
  data: UpdateAccountSiteDetailsPayload,
): Promise<AccountSiteDetails> => {
  const response = await withJWTClient((client) =>
    client.patch<GatewaySiteRecommendationsSlice>(
      withUpdateMask(`${CONNECT_API_V1_BASE_URL}/sites/${siteId}`, data),
      data,
    ),
  );
  return fromGatewaySiteRecommendations(response.data);
};

// --- Tanstack Query Hooks ---
export function useUpdateAccountSiteDetails(siteId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["updateAccountSiteDetails", siteId],
    mutationFn: (data: UpdateAccountSiteDetailsPayload) =>
      updateAccountSiteDetails(siteId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: accountSiteDetailsKeys.all(siteId),
      });
      enqueueSnackbar("Account site details updated successfully", {
        variant: "success",
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to update account site details");
    },
  });
}
